import { sub } from "date-fns";

import { postTrackingData } from "@/api/endpoints/track";
import { SOURCE } from "@/constants";
import { EVENT } from "@/tracking_events";
import { CHART_COLORS_LIGHTER, ensureIsDate, getColor } from "@/utils";

export const datasetLineOptions = (color, isStacked, first) => {
  const options = {
    borderColor: color,
    hoverBorderColor: color,
    pointBackgroundColor: color,
    backgroundColor: color,
    hoverBackgroundColor: color,
    pointBorderColor: color,
    pointHoverBackgroundColor: color,
    pointHitRadius: 20,
    pointBorderWidth: 2,
    pointHoverRadius: 5,
    pointRadius: 5,
    lineTension: 0.3,
    borderWidth: 3,
    fill: false,
  };

  if (isStacked) {
    options.fill = first ? CHART_COLORS_LIGHTER[color] : "-1";
  }

  return options;
};

export const setOptionFromDefault = (idx, isStacked, colorOptions) => {
  const color = getColor(idx, colorOptions);

  return datasetLineOptions(color, isStacked, idx == 0);
};

const sharedOptions = () => {
  const pointRadius = 5;

  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
        },
      },
    },

    layout: {
      padding: {
        left: 0,
        right: Math.floor(pointRadius * 1.5),
        top: Math.floor(pointRadius),
        bottom: 0,
      },
    },

    onHover(e, elements) {
      e.native.target.style.cursor = elements.length ? "pointer" : "default";
    },
  };
};

function dontRenderFirstAndLastLabels(value, index, values) {
  if (values.length > 3 && (index == 0 || index == values.length - 1))
    return "";
  return this.getLabelForValue(value);
}

const valueToLocaleString = (value) => value.toLocaleString();

export const lineOptions = (
  isStacked = true,
  _size = null,
  showYAxisLabel = false,
  yAxisLabelValue = null
) => {
  // Note: Some settings are commented since they are not fully migrated yet.
  return {
    ...sharedOptions(),

    elements: {
      line: {
        fill: "-1",
      },
    },
    scales: {
      x: {
        border: { display: false },
        grid: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 6,
          minRotation: 0,
          maxRotation: 50,
          callback: dontRenderFirstAndLastLabels,
        },
      },

      y: {
        scaleLabel: {
          display: showYAxisLabel,
          labelString: yAxisLabelValue,
        },
        ticks: {
          maxTicksLimit: 5,
          callback: valueToLocaleString,
        },
        border: { display: false },
        grid: {
          display: false,
        },
        stacked: isStacked,
      },
    },
    plugins: {
      ...sharedOptions().plugins,
      tooltip: {
        enabled: true,
        mode: "index",
      },
    },
  };
};

export const pieOptions = () => {
  return {
    ...sharedOptions(),
    cutout: "80%",
    plugins: {
      ...sharedOptions().plugins,
      tooltip: {
        callbacks: {
          title: () => null,
          label: ({ label, formattedValue }) => {
            return ` ${label}: ${formattedValue}%`;
          },
        },
      },
    },
  };
};

export const barOptions = (isStacked = true) => {
  return {
    ...sharedOptions(),
    elements: {
      line: {
        fill: "-1",
      },
    },
    scales: {
      x: {
        time: {
          unit: "date",
        },
        border: { display: false },
        grid: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 6,
          minRotation: 0,
          maxRotation: 50,
          callback: dontRenderFirstAndLastLabels,
        },
        stacked: isStacked,
      },

      y: {
        ticks: {
          maxTicksLimit: 5,
          callback: valueToLocaleString,
        },
        border: { display: false },
        grid: {
          display: false,
        },
        stacked: isStacked,
      },
    },
    plugins: {
      ...sharedOptions().plugins,
      tooltip: {
        enabled: true,
        mode: "index",
      },
    },
  };
};

export const eventPropsForPie = (chartData, props, filterKey) => ({
  onClick(_event, elements) {
    if (!elements || !elements.length) return;

    const item = elements[0];

    if (filterKey) {
      const sources = props.data.map((d) => ({
        id: d.meta.id,
        type: d.meta.type,
      }));

      props.fetchMentions(sources, {
        [filterKey]:
          // This seems like an old hack that uses the fact that the swedish news translation is not the same as the type?
          chartData.labels[item.index] === "Nyhetsartiklar"
            ? SOURCE.NEWS
            : chartData.labels[item.index],
      });
    } else {
      const sources = [
        chartData.datasets[item.datasetIndex].sources[item.index],
      ];

      props.fetchMentions(sources, { metric: props.metric });
    }
  },
});

export const eventPropsForTimeline = (chartData, props, filterKey) => {
  return {
    onClick(_event, elements) {
      if (!elements.length) return;

      postTrackingData(EVENT.WIDGET_CLICK, { widget: props.metric });

      const di = elements[0].datasetIndex;
      const i = elements[0].index;

      const labels = chartData.labels;

      const startDate = i === 0 ? props.startDate : labels[i];
      const endDate =
        i === labels.length - 1
          ? props.endDate
          : sub(ensureIsDate(labels[i + 1]), { days: 1 });

      let sources, filterValue;

      if (filterKey) {
        sources = props.data.map((d) => ({
          id: d.meta.id,
          type: d.meta.type,
        }));

        filterValue = chartData.datasets[di].label;

        props.fetchMentions(sources, {
          dates: [startDate, endDate],
          [filterKey]: filterValue,
          metric: props.metric,
        });
      } else {
        sources = [chartData.datasets[di].source];

        props.fetchMentions(sources, {
          dates: [startDate, endDate],
          metric: props.metric,
        });
      }
    },
  };
};
